/**
 * The form beside each preview. One component, five variants, no per-variant copy
 * of the shared half.
 *
 * THE LAYOUT IS A DELIBERATE STACK OF CARDS, NOT AN AUTO-FLOWING GRID. A first
 * cut let the sections auto-place into the panel's three-column grid, and the
 * result was ragged: a tall publish card beside a five-line colour card, blank
 * cells wherever heights disagreed. So the editor owns the right two-thirds of
 * the panel (`lg:col-span-2` on its root) and stacks three cards — Appearance,
 * the variant's own knobs, the origins card — with the FIELDS laid out
 * two-per-row inside each card. Density lives inside the cards, where unequal
 * heights cost one line, not a grid cell.
 *
 * WHY THE CONFIG IS ONE FLAT OBJECT AND NOT A DISCRIMINATED UNION. A union keyed
 * on the variant reads better in the abstract and is worse here for one concrete
 * reason: the playground holds FIVE of these at once and lets somebody flick
 * between them, so a field only the bar has must keep its value while the reader
 * is looking at the orb. A union would force either a narrowing cast at every
 * setter or five separate states with five separate defaults. So the state is flat
 * and `configFor` projects out only the keys the chosen variant actually has —
 * which is also the shape the embed record wants, because sending `allowMute` on
 * an orb would be storing a field nothing reads.
 *
 * THE TWO `size` FIELDS ARE DELIBERATELY NAMED DIFFERENTLY. The orb's size is a
 * pixel count and the plain button's is `sm | default | lg`; one key holding
 * either would be a `number | string` that every reader has to disambiguate. They
 * are `orbSize` and `buttonSize` in the form and both land on `size` in the
 * projection, where the variant already says which is meant.
 *
 * A COLOUR IS A TEXT FIELD WITH A SWATCH BESIDE IT, NOT A SWATCH. `accent` is
 * documented as a CSS colour, so `currentColor`, a `var(--brand)` and `#0f5` are
 * all legitimate values a customer will want — and none of them fit in an
 * `<input type="color">`, which only speaks 6-digit hex. So the text field is the
 * value and the swatch is a convenience that follows it when it can and says so
 * when it cannot. Rewriting what somebody typed into the nearest hex would be the
 * form quietly changing a field they set on purpose.
 *
 * ORIGINS ARE REJECTED, NEVER NORMALISED. A trailing slash and a path are both
 * things people paste, and both are things this list must not contain — but
 * silently trimming them teaches the reader that anything they paste is fine,
 * which is precisely the wrong lesson for the field that decides where the widget
 * is allowed to run. So each row says what is wrong with it and what to write
 * instead, and the auto-save simply waits until every row is clean — the
 * row-level errors say what it is waiting for.
 *
 * THE PUBLISH HALF IS ABSENT FOR A VIEWER RATHER THAN DISABLED — the rule the
 * users screen and the agent form both follow. The CONFIG half stays live for
 * them, deliberately: changing it writes nothing anywhere, and being able to try
 * the widgets is the entire reason a read-only role is shown this page at all.
 */
import type { ReactNode } from 'react'
import { PlusIcon, XIcon } from 'lucide-react'
import type { EmbedRow, EmbedVariant } from '@/lib/embeds'
import type { VoicePlacement, VoiceWidgetCommon } from '@/components/voice/types'
import { Button } from '@/components/ui/button'
import { Checkbox } from '@/components/ui/checkbox'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { ToggleGroup, ToggleGroupItem } from '@/components/ui/toggle-group'
import type { OrbCanvasTheme } from './orb-canvas'

/**
 * The cap the widgets impose on their own labels, enforced here.
 *
 * The flag's text is rotated ninety degrees inside a fixed-height tab, so it is
 * the tightest of the five and sets the number for all of them. Enforced in the
 * form rather than in the widget because a string that silently truncates in one
 * variant and not another is a bug somebody discovers on a customer's site.
 */
export const LABEL_MAX = 22

/** Everything any of the five widgets can be configured with, in one object. */
export interface PlaygroundConfig extends VoiceWidgetCommon {
  // --- flag
  side: 'left' | 'right'
  /** A CSS length. Not a number: `50%` and `12rem` are both reasonable here. */
  offsetY: string
  width: number
  height: number
  // --- chat flag
  /**
   * The panel header's heading. SEPARATE from `label`, which is the rotated word
   * on the tab: one is capped at 22 characters and phrased as an instruction, the
   * other is the name of whoever answers. See `ChatFlagProps.title`.
   */
  chatTitle: string
  /** The chat window. The four flag fields above size the TAB, not this. */
  chatWidth: number
  chatHeight: number
  /** The panel's own surface, the ink on it, and its hairline — three fields for
   *  the conversation bar's reason: the widget paints a surface rather than
   *  reading theme tokens, and a hairline that is an alpha of the ink cannot be
   *  computed from an arbitrary CSS colour. They ARE this variant's theme. */
  chatSurface: string
  chatOnSurface: string
  chatSurfaceBorder: string
  // --- bar and plain button
  placement: VoicePlacement
  allowText: boolean
  allowMute: boolean
  // --- bar
  /** The launcher's SIDE AND the expanded bar's height. Every other dimension
   *  in that widget scales from it — see conversation-bar.tsx. */
  barSize: number
  /** The expanded bar's own surface, the ink on it, and its hairline. Three fields
   *  because the widget paints its own card rather than reading theme tokens, and a
   *  hairline that is an alpha of the ink cannot be computed from an arbitrary CSS
   *  colour. They are why the bar has no light/dark switch: these ARE its theme. */
  barSurface: string
  barOnSurface: string
  barSurfaceBorder: string
  // --- bars
  /** Bars across the whole stage, mirrored around the centre. */
  barCount: number
  /** Particle density, expressed at the reference stage's size. */
  barsParticles: number
  /** `shadowBlur` on the bars and the particles — the reference's `glowLevel`. */
  barsGlow: number
  /** The stage, at most. It shrinks with the page and keeps its proportion. */
  barsWidth: number
  barsHeight: number
  /** The round call control at the centre of the stage. */
  barsButtonSize: number
  /** Between the stage and the caption under it. */
  barsCaptionGap: number
  barsRadius: number
  /** The stage's surface, the button's surface, and the glyph on it. Separate
   *  fields because "white button on a white stage" is only an object once it has
   *  a shadow, and a customer with a dark page needs all three to move. */
  barsBackground: string
  barsButtonColor: string
  /** Empty means "follow the accent", which is how the reference draws its mic. */
  barsIconColor: string
  /** The caption sits OUTSIDE the stage, on the host page, so it gets its own
   *  neutral colour rather than the accent. */
  barsCaptionColor: string
  // --- orb
  orbSize: number
  /** Which orb look: orb-ui's cloud (default), the plain circle, or the radial
   *  pinwheel. Named apart from `theme`, which is the light/dark scheme. */
  orbTheme: OrbCanvasTheme
  colors: { from: string; to: string }
  // --- plain button
  buttonSize: 'sm' | 'default' | 'lg'
  showTimer: boolean
  // --- hover pill
  /** The word the fill sweep reveals. Its own field because `label` is the resting
   *  word and BOTH are painted — one under the other's wipe. */
  pillHoverLabel: string
  pillWidth: number
  pillHeight: number
  /** The resting pill, its ink, and the 2px ring. Three fields for the chat
   *  panel's reason, plus one: the ring is a third colour in the reference's own
   *  design (indigo on white with an orange dot) and is not derivable from
   *  either of the other two. */
  pillSurface: string
  pillOnSurface: string
  pillBorder: string
  /** The reference's 46 ms click when the pointer arrives. On by default, because
   *  that is what the reference does — and a switch, because unrequested audio on
   *  somebody else's page is the deployer's call. */
  pillSound: boolean
  pillMeterBars: number
}

/** Where a floating bar or button may sit. The four corners, plus in-flow. */
const CORNERS: VoicePlacement[] = [
  'bottom-right',
  'bottom-left',
  'top-right',
  'top-left',
  'inline',
]

const THEMES: NonNullable<VoiceWidgetCommon['theme']>[] = ['auto', 'light', 'dark']

/**
 * Sensible starting values, per variant.
 *
 * The differences are only where a shared default would be wrong: the orb is not
 * a corner-dwelling widget, so it starts inline, and the flag ignores placement
 * entirely. Everything else starts the same across the five so that a reader
 * comparing them is comparing the widgets and not five sets of colours — with two
 * colour exceptions. The flag starts in the reference widget's own livery, #fac800
 * yellow with black type, because that tab IS a brand object and the component's
 * own defaults say the same thing (see ACCENT/ON_ACCENT in flag-button.tsx). A
 * blue seed here used to override those defaults in every generated snippet, so
 * the "same yellow tab the customer has had for a year" promise held only for
 * pages that never touched the playground.
 *
 * FROM 2026-08-25 THE YELLOW IS THE HOUSE COLOUR AND FOUR OF THE FIVE SEED IT.
 * The conversation bar shares the flag's livery outright in its own component (see
 * ACCENT in conversation-bar.tsx), the orb and the plain button seed it here, and
 * the blue is gone: five widgets in five colours made the playground look like five
 * products. Bars is the one exception — violet, which is that widget's own default
 * since the same day, because an 800 px stage IS its colour in a way a 56 px button
 * is not.
 *
 * The plain button is a seed ONLY, and deliberately: its component leaves `accent`
 * undefined so an unstyled tag inherits the host page's own button colours, which is
 * the entire point of that variant. Here it starts yellow because a playground is
 * where somebody is choosing a look, not inheriting one.
 */
export function defaultConfig(variant: EmbedVariant): PlaygroundConfig {
  return {
    // The flag's wording matches its component default (flag-button.tsx), same
    // rule as its colours below: the seed must not silently override the brand
    // object's own defaults in every generated snippet.
    label:
      variant === 'flag' || variant === 'chat' ? 'Click to Talk'
      : variant === 'pill' ? 'Agent Ready'
      : 'Talk to us',
    endLabel: 'End call',
    accent: variant === 'bars' ? '#9600fa' : variant === 'pill' ? '#f97316' : '#fac800',
    onAccent: variant === 'bars' || variant === 'pill' ? '#ffffff' : '#000000',
    caption: 'Powered by Voice AI',
    theme: 'auto',
    side: 'right',
    offsetY: '50%',
    width: 44,
    height: 148,
    // The reference prototype's widget box, and its own component defaults. The
    // panel's card stays light while the tab is yellow: they are two objects, and
    // a transcript is something to read.
    chatTitle: 'Voice Assistant',
    chatWidth: 380,
    chatHeight: 560,
    chatSurface: '#ffffff',
    chatOnSurface: '#0a0a0a',
    chatSurfaceBorder: 'rgba(0, 0, 0, 0.12)',
    placement: variant === 'orb' ? 'inline' : 'bottom-right',
    allowText: true,
    allowMute: true,
    // 56 is upstream's row height, so the default renders the ElevenLabs bar's
    // proportions exactly; the light card is the bar's own default livery, stated
    // here as well because a seed that silently overrides a component default is
    // how a "default" stops meaning anything (the same trap the flag's colours
    // fell into — see the note above). The card stays light while the BUTTON is
    // yellow: they are two objects, and the bar is a surface to read a waveform on.
    barSize: 56,
    barSurface: '#ffffff',
    barOnSurface: '#0a0a0a',
    barSurfaceBorder: 'rgba(0, 0, 0, 0.12)',
    // The bars defaults ARE the reference visualizer's geometry: a 64-bar, 800x300
    // white stage with 150 particles and a 20 px glow. Only the colour differs from
    // that reference, and it differs in the component too. See bars-visualizer.tsx.
    barCount: 64,
    barsParticles: 150,
    barsGlow: 20,
    barsWidth: 800,
    barsHeight: 300,
    barsButtonSize: 56,
    barsCaptionGap: 12,
    barsRadius: 16,
    barsBackground: '#ffffff',
    barsButtonColor: '#ffffff',
    barsIconColor: '',
    barsCaptionColor: '#64748b',
    orbSize: 128,
    orbTheme: 'cloud',
    colors: { from: '#1d4ed8', to: '#7c3aed' },
    buttonSize: 'default',
    showTimer: true,
    // THE REFERENCE'S OWN LIVERY, not the house yellow, and that is the one place
    // this file seeds a variant off-palette on purpose: the whole point of this
    // variant is that it IS the voice2me button, and a customer who asked for that
    // button and got a yellow one has not been given what they asked for. Its
    // component defaults say the same thing, so a bare tag renders it too.
    pillHoverLabel: 'Start Chat',
    pillWidth: 176,
    pillHeight: 48,
    pillSurface: '#ffffff',
    pillOnSurface: '#0a0a0a',
    pillBorder: '#363576',
    pillSound: true,
    pillMeterBars: 5,
  }
}

/**
 * The subset of the config this variant actually reads, ready to store on the
 * embed record. See the file header for why this projection exists.
 */
export function configFor(variant: EmbedVariant, c: PlaygroundConfig): Record<string, unknown> {
  const common = {
    label: c.label,
    endLabel: c.endLabel,
    accent: c.accent,
    onAccent: c.onAccent,
    caption: c.caption,
    theme: c.theme,
  }
  switch (variant) {
    case 'flag':
      /**
       * NO `theme`. The flag is the one widget that is a brand object rather than
       * a themed one: `flag-button.tsx` has not a single class name in it, and the
       * light/dark switch works by putting `vai-dark`/`vai-light` on a wrapper
       * inside the shadow root for CLASS-BASED widgets to key off. The flag reads
       * no tokens, so the class landed on an ancestor of a widget that could not
       * see it — a control that has never changed one pixel of this variant.
       * `accent` and `onAccent` are its light and dark, and they are two fields up.
       */
      return {
        label: c.label,
        endLabel: c.endLabel,
        accent: c.accent,
        onAccent: c.onAccent,
        caption: c.caption,
        side: c.side,
        offsetY: c.offsetY,
        width: c.width,
        height: c.height,
      }
    case 'chat':
      /**
       * NO `theme`, for the flag's reason one case up: `chat-flag.tsx` contains no
       * class name either, so the switch would land on an ancestor of a widget
       * that cannot see it. The difference from the flag is that this variant DOES
       * paint a large surface, so it carries the conversation bar's three colours
       * instead — those are its theme, and they are real.
       *
       * `title` is here and `label` is too, and they are not a duplicate: the tab
       * paints one and the panel header paints the other.
       */
      return {
        label: c.label,
        endLabel: c.endLabel,
        title: c.chatTitle,
        caption: c.caption,
        accent: c.accent,
        onAccent: c.onAccent,
        side: c.side,
        offsetY: c.offsetY,
        width: c.width,
        height: c.height,
        panelWidth: c.chatWidth,
        panelHeight: c.chatHeight,
        surface: c.chatSurface,
        onSurface: c.chatOnSurface,
        surfaceBorder: c.chatSurfaceBorder,
      }
    case 'bar':
      /**
       * NO `caption` AND NO `theme`, and both absences are the point of the
       * 2026-08-24 redesign rather than an oversight.
       *
       * The variant is a launcher now (see conversation-bar.tsx): there is nowhere
       * in a 56 px tile for a "powered by" line that would not be a second
       * floating object on somebody's page, and a light/dark switch is a control
       * that lies once the widget paints its own surface — `surface`, `onSurface`
       * and `surfaceBorder` ARE its theme. Both keys were ALREADY ignored by this
       * variant before the redesign; the difference is that the editor no longer
       * offers them, so what is saved is what renders.
       */
      return {
        label: c.label,
        accent: c.accent,
        onAccent: c.onAccent,
        placement: c.placement,
        size: c.barSize,
        surface: c.barSurface,
        onSurface: c.barOnSurface,
        surfaceBorder: c.barSurfaceBorder,
        allowText: c.allowText,
        allowMute: c.allowMute,
      }
    case 'bars':
      /**
       * NO `placement` AND NO `theme`, and both absences are deliberate.
       *
       * Bars is inline-only (see the bars case in embed/main.tsx): it is a stage,
       * not a launcher, and a corner-pinned 800-wide spectrum is a worse page in
       * every case. `theme` is dropped because this variant paints its own
       * surface — the `background` field below IS its theme, and a light/dark
       * switch that changes nothing is a control that lies.
       */
      return {
        label: c.label,
        caption: c.caption,
        accent: c.accent,
        barCount: c.barCount,
        particles: c.barsParticles,
        glow: c.barsGlow,
        width: c.barsWidth,
        height: c.barsHeight,
        buttonSize: c.barsButtonSize,
        captionGap: c.barsCaptionGap,
        radius: c.barsRadius,
        background: c.barsBackground,
        buttonColor: c.barsButtonColor,
        iconColor: c.barsIconColor,
        captionColor: c.barsCaptionColor,
        allowMute: c.allowMute,
      }
    case 'orb':
      /** NO `endLabel` — see the note on `readsEndLabel`. The orb's visible line is
       *  written from the phase ("Listening", "Talk to interrupt"), never from this. */
      return {
        label: c.label,
        accent: c.accent,
        onAccent: c.onAccent,
        caption: c.caption,
        theme: c.theme,
        size: c.orbSize,
        orbTheme: c.orbTheme,
        colors: c.colors,
      }
    case 'button':
      return {
        ...common,
        size: c.buttonSize,
        placement: c.placement,
        showTimer: c.showTimer,
      }
    case 'pill':
      /**
       * NO `theme` — no class name in `hover-pill.tsx` for it to reach — and NO
       * `endLabel`, because the live pill is a stop disc and two meters and there
       * is nowhere to paint a word. `hoverLabel` is the string that IS painted
       * during the call-to-action, which is what an operator actually wants to
       * translate.
       */
      return {
        label: c.label,
        hoverLabel: c.pillHoverLabel,
        caption: c.caption,
        accent: c.accent,
        onAccent: c.onAccent,
        placement: c.placement,
        width: c.pillWidth,
        height: c.pillHeight,
        surface: c.pillSurface,
        onSurface: c.pillOnSurface,
        border: c.pillBorder,
        sound: c.pillSound,
        allowMute: c.allowMute,
        meterBars: c.pillMeterBars,
      }
  }
}

/**
 * What is wrong with this origin, or null when nothing is.
 *
 * The comparison that does the real work is `url.origin !== typed`: it catches a
 * trailing slash, a path, a query, a fragment, credentials and an upper-case host
 * in one line, because `URL` already knows what the canonical origin of each of
 * them is. The named cases below only exist to say WHICH of those it was, since
 * "not a bare origin" on its own leaves the reader hunting.
 */
export function originProblem(raw: string): string | null {
  const v = raw.trim()
  if (v === '') return 'Empty. Type an origin or remove the row.'
  if (v.includes('*')) {
    return 'No wildcards. One typo in a wildcard opens the widget to every subdomain anybody can register under that name, so each origin is listed in full.'
  }
  let url: URL
  try {
    url = new URL(v)
  } catch {
    return 'Not a URL. Start with https:// — or http:// for localhost.'
  }
  if (url.protocol !== 'https:' && url.protocol !== 'http:') {
    return `Scheme ${url.protocol} is not http or https, and a browser will never send this as an Origin header.`
  }
  if (url.username !== '' || url.password !== '') {
    return 'No credentials. Write just the scheme, host and port.'
  }
  if (url.origin !== v) {
    if (v.endsWith('/')) return `No trailing slash. Write ${url.origin}`
    if (url.pathname !== '/') return `No path. Write just the origin: ${url.origin}`
    if (url.search !== '' || url.hash !== '') {
      return `No query or fragment. Write just the origin: ${url.origin}`
    }
    return `Not a bare origin. Write ${url.origin}`
  }
  return null
}

/** True when every row is a valid bare origin. An empty list is clean but useless. */
export function originsClean(origins: readonly string[]): boolean {
  return origins.every((o) => originProblem(o) === null)
}

// ===========================================================================
// The panel
// ===========================================================================

export function ConfigEditor({
  variant,
  value,
  onChange,
  canWrite,
  origins,
  onOriginsChange,
  originsSave,
}: {
  variant: EmbedVariant
  value: PlaygroundConfig
  /**
   * AN UPDATER, NOT A FINISHED OBJECT, and it is not stylistic.
   *
   * The obvious signature is `(next: PlaygroundConfig) => void` with the caller
   * building `{ ...value, ...changes }`. That `value` is the one from THIS render,
   * so two changes landing in a single React batch — the colour swatch and its hex
   * field firing together, a paste that touches two inputs, anything scripted —
   * both derive from the same base and the second silently discards the first. It
   * showed up the first time this page was driven from a browser: the label updated
   * and the accent set in the same tick did not.
   */
  onChange: (update: (prev: PlaygroundConfig) => PlaygroundConfig) => void
  /** False for a viewer. The publish half below is not rendered at all. */
  canWrite: boolean
  origins: string[]
  /** An updater, for the same reason `onChange` is one. */
  onOriginsChange: (update: (prev: string[]) => string[]) => void
  /** The auto-save's current verdict, for the one-line status under the list. */
  originsSave: { kind: 'saving' | 'saved' | 'error'; message?: string } | null
}) {
  /**
   * One setter taking a partial, for the reason given on the agent form: a
   * generic `set(key, value)` widens `{ ...prev, [key]: v }` to an index signature
   * and the compiler stops checking that `side` only ever receives `left` or
   * `right`. The merge happens inside the updater, never against `value`.
   */
  const set = (changes: Partial<PlaygroundConfig>) =>
    onChange((prev) => ({ ...prev, ...changes }))
  /** The one nested field, and the reason it does not go through `set`: a partial
   *  merge would replace the whole `colors` object with a half-built one. */
  const setColour = (which: 'from' | 'to', v: string) =>
    onChange((prev) => ({ ...prev, colors: { ...prev.colors, [which]: v } }))
  const id = (name: string) => `${variant}-${name}`
  const labelLength = (value.label ?? '').length

  /**
   * WHICH SHARED APPEARANCE FIELDS THIS VARIANT ACTUALLY READS.
   *
   * The Appearance card is shared by all six widgets, and a shared card is how a
   * setting ends up saved and ignored: the conversation bar has never rendered a
   * caption, and `theme` was write-only for every variant until the embed learnt to
   * set a class. A form that offers a control the widget drops on the floor teaches
   * the reader that the whole form might be decorative, so the fields the variant
   * ignores are absent rather than present-and-inert. The bar's replacement for
   * `theme` is its own three surface colours, which are real — see its card below
   * and `configFor`; Bars replaced it with its own Background, for the same reason
   * and on the same day, and never read `onAccent` at all — that card offered two
   * accent colours where only one of them did anything.
   */
  const readsCaption = variant !== 'bar'
  /**
   * THE LIGHT/DARK SWITCH REACHES CLASS-BASED WIDGETS ONLY, which is two of the six.
   *
   * `theme` works by putting `vai-dark` / `vai-light` on a wrapper inside the shadow
   * root; the tokens in `embed.css` key off that class, and Tailwind classes read the
   * tokens. The orb and the plain button are built from those classes, so the switch
   * genuinely repaints them. The conversation bar, Bars and the two flags do not contain
   * a single class name — every colour in them is an inline style — so the class landed
   * on an ancestor of a widget that could not see it. The first two lost the field on
   * 2026-08-24; the flag loses it now, and for a plainer reason than "it paints its
   * own surface": the flag is a BRAND object, `accent` and `onAccent` are its livery,
   * and a yellow tab does not have a dark mode. The chat flag is that same tab with a
   * panel behind it, and the panel's three surface colours are its theme instead.
   */
  const readsTheme = variant === 'orb' || variant === 'button'
  const readsOnAccent = variant !== 'bars'
  /**
   * AND THE END LABEL IS COPY ON THREE OF THE SIX.
   *
   * The flag paints it inside the expanded pill, next to the stop square; the plain
   * button paints it as the button's own text; the chat flag paints it on the pill
   * under its composer — on those three it is the visible word for "this call is
   * running, press to stop", and a customer who is not writing in English needs to
   * set it.
   *
   * On the other three there is nothing to paint it on: the control is an icon, and
   * `endLabel` only ever reached a tooltip and a screen reader — while `label`, one
   * field above, was already doing exactly that job before the call started. Two
   * fields for the two halves of one icon button's name is a form asking an operator
   * to make a decision that has no visible outcome. The widgets keep the prop and
   * their defaults, so a hand-written tag carrying `data-end-label` is still honoured
   * and a translated site is not locked out; what goes is the field.
   */
  const readsEndLabel = variant === 'flag' || variant === 'chat' || variant === 'button'
  /** The hover pill's second word, and the only variant that has one — see its
   *  card below and the note on `PlaygroundConfig.pillHoverLabel`. */
  /**
   * ON TWO VARIANTS THESE TWO FIELDS ARE NOT COPY, AND THE FORM HAS TO SAY SO.
   *
   * "Label" and "End label" mean what they say on the flag, the plain button and the
   * orb: a string the visitor reads off the control. On the conversation bar and on
   * Bars the control is an icon, and the string is its NAME — the native tooltip on
   * hover and the text a screen reader announces. Nothing paints it. (The bar used
   * to paint it, faintly, inside the waveform tray; the tray only exists during a
   * call and the label only existed when there was not one, so the two conditions
   * never overlapped except for about 200 ms of the closing animation. That label is
   * gone — see conversation-bar.tsx.)
   *
   * Two fields called "Label" that behave differently in different variants is the
   * form telling a half-truth in one of them, and the half-truth is the expensive
   * kind: an operator types a shop-window sentence, saves, looks at their page and
   * finds nothing changed. Renaming them here costs nothing — the stored key, the
   * `data-label` attribute and the widget prop are all untouched.
   */
  const labelIsTooltip = variant === 'bar' || variant === 'bars'

  return (
    <div className="flex min-w-0 flex-col gap-4 lg:col-span-2">
      <Section title="Appearance">
        <div className="grid gap-x-4 gap-y-3 sm:grid-cols-2">
        <div className="field">
          <Label
            htmlFor={id('label')}
            title={
              labelIsTooltip ?
                `The call button's hover tooltip and the name a screen reader reads out, before a call starts. Nothing on the page paints it. Capped at ${LABEL_MAX} characters, which is the flag's limit shared by every variant.`
              : `Shown before the call starts. Capped at ${LABEL_MAX} characters — the flag's text is rotated inside a fixed-height tab, and a longer string would be cut off there and nowhere else.`
            }
          >
            {labelIsTooltip ? 'Button tooltip' : 'Label'}
            {/* A position indicator, never an error: `maxLength` below stops the
                field at the cap, paste included, so this cannot read over it. The
                count is here because a limit with no counter is a limit somebody
                discovers by having a keystroke ignored. */}
            <span className="ml-auto text-xs font-normal text-muted-foreground">
              {labelLength}/{LABEL_MAX}
            </span>
          </Label>
          <Input
            id={id('label')}
            value={value.label ?? ''}
            maxLength={LABEL_MAX}
            onChange={(e) => set({ label: e.target.value })}
          />
        </div>

        {readsEndLabel && (
          <div className="field">
            <Label
              htmlFor={id('endLabel')}
              title="The word on the control while a call is running. Painted only on the two variants that still offer this — see the note on the Label field."
            >
              End label
            </Label>
            <Input
              id={id('endLabel')}
              value={value.endLabel ?? ''}
              maxLength={LABEL_MAX}
              onChange={(e) => set({ endLabel: e.target.value })}
            />
          </div>
        )}

        {/* A FIELD THE VARIANT IGNORES IS NOT SHOWN. Both of these were saved and
            then dropped on the floor by the conversation bar for as long as it has
            existed, which is a form dictating appearance that never arrives — the
            worst kind of control. See `readsCaption` / `readsTheme`. */}
        {readsCaption && (
          <div className="field">
            <Label htmlFor={id('caption')}>Caption</Label>
            <Input
              id={id('caption')}
              value={value.caption ?? ''}
              onChange={(e) => set({ caption: e.target.value })}
            />
          </div>
        )}

        {readsTheme && (
          <div className="field">
            <Label htmlFor={id('theme')}>Theme</Label>
            <Select
              value={value.theme ?? 'auto'}
              onValueChange={(v) => set({ theme: v as PlaygroundConfig['theme'] })}
            >
              <SelectTrigger id={id('theme')} size="sm" className="w-full">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {THEMES.map((t) => (
                  <SelectItem key={t} value={t}>
                    <span className="mono">{t}</span>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        )}

        <ColourField
          id={id('accent')}
          label="Accent"
          value={value.accent ?? ''}
          onChange={(v) => set({ accent: v })}
        />
        {readsOnAccent && (
          <ColourField
            id={id('onAccent')}
            label="On accent"
            value={value.onAccent ?? ''}
            onChange={(v) => set({ onAccent: v })}
          />
        )}
        </div>

        {/* One footnote for the whole card. What it says depends on what the
            variant actually reads: two of the five have replaced the shared
            theme with colours of their own, and the sentence has to follow. */}
        {variant === 'bars' ? (
          <p className="text-xs text-muted-foreground">
            Any CSS colour works, and the swatch only follows 6-digit hex.{' '}
            <span className="font-medium">Accent</span> is the whole picture here — the
            mirrored spectrum, the particles and their glow, and the mic glyph unless the
            Bars card overrides it. The text field above never appears on the stage: it
            names the round control for a tooltip and a screen reader.
          </p>
        ) : (
          <p className="text-xs text-muted-foreground">
            Any CSS colour works in the two colour fields; the swatch only follows 6-digit
            hex. <span className="font-medium">On accent</span> is the text and icons ON the
            accent surface — set it deliberately.{' '}
            {readsTheme ? (
              <>
                Theme <span className="mono">auto</span> follows the host page; the preview
                switch in the bar above changes the page underneath the widget, not the
                widget.
              </>
            ) : variant === 'pill' ? (
              <>
                These two are the <em>hover</em> half of the button: the orange the dot
                sweeps out into, and the white word and microphone that ride on it. The
                pill at rest has three colours of its own below, which is why this
                variant has no Theme field.
              </>
            ) : variant === 'chat' ? (
              <>
                The tab is these two — the yellow and the black drawn on it — and so are
                the visitor&rsquo;s message bubbles and the send button inside the panel.
                The panel&rsquo;s own card has three colours of its own below, which is
                why this variant has no Theme field: it contains no stylesheet and no
                class name for a light/dark switch to reach.
              </>
            ) : variant === 'flag' ? (
              <>
                These two <em>are</em> the flag: the tab&rsquo;s yellow and the black drawn
                on it. That is also why it has no Theme field — the widget contains no
                stylesheet and no class name for a light/dark switch to reach, so the
                switch never changed a pixel of it. A dark-page flag is a darker{' '}
                <span className="font-medium">Accent</span>.
              </>
            ) : (
              <>
                Here the two are the call button itself — its tile and the phone glyph on
                it — and they default to the flag&rsquo;s yellow on black, because the two
                floating launchers are one product. The bar it opens into has its own three
                colours below, which is why this variant has no light/dark switch. The text
                field above never appears on the page either: it is the button&rsquo;s
                tooltip and its screen-reader name, before and during a call alike.
              </>
            )}
          </p>
        )}
      </Section>

      {variant === 'flag' && (
        <Section title="Flag">
          <div className="grid gap-x-4 gap-y-3 sm:grid-cols-2">
            <div className="field">
              <Label>Side</Label>
              <ToggleGroup
                type="single"
                variant="outline"
                size="sm"
                spacing={0}
                value={value.side}
                // Radix hands back '' when somebody re-presses the active item.
                // Ignoring that keeps the flag on a side rather than nowhere.
                onValueChange={(v) => {
                  if (v === 'left' || v === 'right') set({ side: v })
                }}
              >
                <ToggleGroupItem value="left">Left</ToggleGroupItem>
                <ToggleGroupItem value="right">Right</ToggleGroupItem>
              </ToggleGroup>
            </div>
            <div className="field">
              <Label htmlFor={id('offsetY')}>Offset from the top</Label>
              <Input
                id={id('offsetY')}
                value={value.offsetY}
                onChange={(e) => set({ offsetY: e.target.value })}
                placeholder="50%"
                className="mono"
              />
              <p className="text-xs text-muted-foreground">
                A CSS length. <span className="mono">50%</span> centres it;{' '}
                <span className="mono">12rem</span> pins it below a fixed header.
              </p>
            </div>
            <NumberField
              id={id('width')}
              label="Width"
              value={value.width}
              min={24}
              max={120}
              onChange={(n) => set({ width: n })}
            />
            <NumberField
              id={id('height')}
              label="Height"
              value={value.height}
              min={60}
              max={320}
              onChange={(n) => set({ height: n })}
            />
          </div>
        </Section>
      )}

      {variant === 'chat' && (
        <>
          {/* THE TAB AND THE PANEL ARE TWO BOXES, so they get two cards. The four
              fields here size and place the tab — the same four the flag has, and
              deliberately the same names — and the card below sizes the window it
              opens. Putting all eight in one grid produced two "Width" fields side
              by side, which is a form inviting the wrong one to be typed in. */}
          <Section title="Tab">
            <div className="grid gap-x-4 gap-y-3 sm:grid-cols-2">
              <div className="field">
                <Label>Side</Label>
                <ToggleGroup
                  type="single"
                  variant="outline"
                  size="sm"
                  spacing={0}
                  value={value.side}
                  // Radix hands back '' when somebody re-presses the active item.
                  // Ignoring that keeps the tab on a side rather than nowhere.
                  onValueChange={(v) => {
                    if (v === 'left' || v === 'right') set({ side: v })
                  }}
                >
                  <ToggleGroupItem value="left">Left</ToggleGroupItem>
                  <ToggleGroupItem value="right">Right</ToggleGroupItem>
                </ToggleGroup>
                <p className="text-xs text-muted-foreground">
                  The panel opens out of this edge, and the tab rides out with it to sit
                  against the panel&rsquo;s inner face.
                </p>
              </div>
              <div className="field">
                <Label htmlFor={id('offsetY')}>Offset from the top</Label>
                <Input
                  id={id('offsetY')}
                  value={value.offsetY}
                  onChange={(e) => set({ offsetY: e.target.value })}
                  placeholder="50%"
                  className="mono"
                />
                <p className="text-xs text-muted-foreground">
                  A CSS length, naming the centre of the closed tab.{' '}
                  <span className="mono">50%</span> centres it. While the panel is open the
                  tab docks to the panel&rsquo;s centre instead, so the two always line up.
                </p>
              </div>
              <NumberField
                id={id('width')}
                label="Tab width"
                value={value.width}
                min={24}
                max={120}
                onChange={(n) => set({ width: n })}
              />
              <NumberField
                id={id('height')}
                label="Tab height"
                value={value.height}
                min={60}
                max={320}
                onChange={(n) => set({ height: n })}
              />
            </div>
          </Section>

          <Section title="Chat panel">
            <div className="grid gap-x-4 gap-y-3 sm:grid-cols-2">
              <div className="field sm:col-span-2">
                <Label
                  htmlFor={id('chatTitle')}
                  title="The heading in the panel's own header — the name of whoever answers. The Label field above is the rotated word on the tab, which is a different string doing a different job."
                >
                  Panel title
                </Label>
                <Input
                  id={id('chatTitle')}
                  value={value.chatTitle}
                  onChange={(e) => set({ chatTitle: e.target.value })}
                />
                <p className="text-xs text-muted-foreground">
                  Read at 14px across the header, so it is not capped the way{' '}
                  <span className="font-medium">Label</span> is. The{' '}
                  <span className="font-medium">Caption</span> above sits under it as the
                  small second line.
                </p>
              </div>
              <NumberField
                id={id('chatWidth')}
                label="Panel width"
                value={value.chatWidth}
                min={260}
                max={560}
                hint="Shrinks with the window so the tab beside it always stays on screen."
                onChange={(n) => set({ chatWidth: n })}
              />
              <NumberField
                id={id('chatHeight')}
                label="Panel height"
                value={value.chatHeight}
                min={320}
                max={900}
                hint="Capped at the window height less 32px, whatever is typed here."
                onChange={(n) => set({ chatHeight: n })}
              />
              <ColourField
                id={id('chatSurface')}
                label="Panel surface"
                value={value.chatSurface}
                onChange={(v) => set({ chatSurface: v })}
              />
              <ColourField
                id={id('chatOnSurface')}
                label="Panel ink"
                value={value.chatOnSurface}
                onChange={(v) => set({ chatOnSurface: v })}
              />
              <ColourField
                id={id('chatSurfaceBorder')}
                label="Hairline"
                value={value.chatSurfaceBorder}
                onChange={(v) => set({ chatSurfaceBorder: v })}
                hint="The border under the header, around the agent's bubbles and around the composer. Usually the ink at a low alpha — it is its own field because an alpha of an arbitrary CSS colour is not something the widget can compute."
              />
            </div>
            <p className="text-xs text-muted-foreground">
              These three are the panel&rsquo;s theme, and they are why this variant has no
              light/dark switch. For a dark site, a near-black surface with near-white ink
              and a white-at-low-alpha hairline is the whole change — the yellow accent
              above keeps working against both.
            </p>
          </Section>
        </>
      )}

      {variant === 'bar' && (
        <>
          {/* NO `inline` IN THE PLACEMENT LIST, and no Side field. The corner is
              the whole of the positioning: a launcher in a right-hand corner opens
              LEFT and one in a left-hand corner opens RIGHT, because the other
              direction grows off the side of the window. A field for it would be a
              field whose wrong value is always wrong. */}
          <Section title="Call button">
            <div className="grid gap-x-4 gap-y-3 sm:grid-cols-2">
              <PlacementField
                id={id('placement')}
                value={value.placement}
                onChange={(p) => set({ placement: p })}
                cornersOnly
                hint={
                  <>
                    The corner the button parks in, 16 px off both edges. It opens{' '}
                    <span className="font-medium">away from the nearest side</span> — right
                    corners open left, left corners open right — so the direction is not a
                    separate setting.
                  </>
                }
              />
              <NumberField
                id={id('barSize')}
                label="Button size"
                value={value.barSize}
                min={40}
                max={96}
                onChange={(n) => set({ barSize: n })}
                hint="The button's side, and the height of the bar it opens into. Everything else scales from it: 56 is the ElevenLabs bar's own proportions, exactly."
              />
              <div className="flex flex-col gap-3">
                <CheckField
                  id={id('allowText')}
                  label="Text input"
                  checked={value.allowText}
                  onChange={(on) => set({ allowText: on })}
                  hint="Lets a visitor type instead of speaking — the only path that works with no microphone."
                />
                <CheckField
                  id={id('allowMute')}
                  label="Mute button"
                  checked={value.allowMute}
                  onChange={(on) => set({ allowMute: on })}
                  hint="Holds the session open and stops sending audio. Does not end the call."
                />
              </div>
              <p className="self-end text-xs text-muted-foreground">
                Both controls live in the opened bar and are only pressable during a call.
                Turning them off narrows the bar rather than leaving a gap. The hang-up
                control is the call button itself.
              </p>
            </div>
          </Section>

          <Section title="Call button — bar colours">
            <div className="grid gap-x-4 gap-y-3 sm:grid-cols-2">
              <ColourField
                id={id('barSurface')}
                label="Bar background"
                value={value.barSurface}
                onChange={(v) => set({ barSurface: v })}
                hint="The card that opens out of the button. transparent lets the host page show through, which is rarely what you want behind a waveform."
              />
              <ColourField
                id={id('barOnSurface')}
                label="Bar text and waveform"
                value={value.barOnSurface}
                onChange={(v) => set({ barOnSurface: v })}
                hint="The waveform's bars, the mute and keyboard glyphs, and the message you type."
              />
              <ColourField
                id={id('barSurfaceBorder')}
                label="Bar hairline"
                value={value.barSurfaceBorder}
                onChange={(v) => set({ barSurfaceBorder: v })}
                hint="The 1 px rule around the bar and the message box. Stated rather than derived, because an alpha of an arbitrary CSS colour cannot be computed in the widget."
              />
              <p className="self-end text-xs text-muted-foreground">
                These three are this variant&rsquo;s theme — it paints its own card instead
                of reading the page&rsquo;s light or dark scheme, which is why there is no
                Theme field. For a dark page, try{' '}
                <span className="mono">#111114</span> /{' '}
                <span className="mono">#f5f5f7</span> /{' '}
                <span className="mono">rgba(255,255,255,0.14)</span>.
              </p>
            </div>
          </Section>
        </>
      )}

      {variant === 'bars' && (
        <>
          {/* NO PLACEMENT FIELD. Bars is inline-only — it is a stage rather than a
              launcher, and the four corners produced a widget that covered a third
              of a phone screen with no way to collapse it. */}
          <Section title="Bars">
            <div className="grid gap-x-4 gap-y-3 sm:grid-cols-2">
              <NumberField
                id={id('barCount')}
                label="Bars"
                value={value.barCount}
                min={8}
                max={160}
                step={2}
                onChange={(n) => set({ barCount: n })}
                hint="Mirrored, so this is rounded to an even number. 64 is the reference; 16 reads as a chunky equaliser, 128 as a fine comb."
              />
              <NumberField
                id={id('barsParticles')}
                label="Particles"
                value={value.barsParticles}
                min={0}
                max={600}
                onChange={(n) => set({ barsParticles: n })}
                hint="A density, not a count: it is scaled by the stage's area, so a smaller stage keeps the same look. 0 turns the field off."
              />
              <NumberField
                id={id('barsGlow')}
                label="Glow"
                value={value.barsGlow}
                min={0}
                max={80}
                onChange={(n) => set({ barsGlow: n })}
                hint="Blur radius behind every bar and particle. The reference uses 20; 0 gives flat bars."
              />
              <NumberField
                id={id('barsRadius')}
                label="Corner radius"
                value={value.barsRadius}
                min={0}
                max={200}
                onChange={(n) => set({ barsRadius: n })}
              />
            </div>
          </Section>

          <Section title="Bars — size and spacing">
            <div className="grid gap-x-4 gap-y-3 sm:grid-cols-2">
              <NumberField
                id={id('barsWidth')}
                label="Width"
                value={value.barsWidth}
                min={160}
                max={2400}
                onChange={(n) => set({ barsWidth: n })}
                hint="At most. The stage shrinks with the page."
              />
              <NumberField
                id={id('barsHeight')}
                label="Height"
                value={value.barsHeight}
                min={80}
                max={1200}
                onChange={(n) => set({ barsHeight: n })}
                hint="At full width. Narrower pages keep this proportion rather than this number, so the picture never squashes."
              />
              <NumberField
                id={id('barsButtonSize')}
                label="Button size"
                value={value.barsButtonSize}
                min={28}
                max={160}
                onChange={(n) => set({ barsButtonSize: n })}
              />
              <NumberField
                id={id('barsCaptionGap')}
                label="Caption gap"
                value={value.barsCaptionGap}
                min={0}
                max={96}
                onChange={(n) => set({ barsCaptionGap: n })}
                hint="Between the bottom of the stage and the caption under it."
              />
              <CheckField
                id={id('barsAllowMute')}
                label="Mute button"
                checked={value.allowMute}
                onChange={(on) => set({ allowMute: on })}
                hint="Beside the call control while a call is live, in the button and icon colours. Holds the session open and stops sending audio; the centre control is still the hang-up."
              />
            </div>
          </Section>

          <Section title="Bars — colours">
            <div className="grid gap-x-4 gap-y-3 sm:grid-cols-2">
              <ColourField
                id={id('barsBackground')}
                label="Background"
                value={value.barsBackground}
                onChange={(v) => set({ barsBackground: v })}
                hint="The stage's surface. transparent lets the host page show through."
              />
              <ColourField
                id={id('barsButtonColor')}
                label="Button"
                value={value.barsButtonColor}
                onChange={(v) => set({ barsButtonColor: v })}
                hint="The round control's own surface. It carries a shadow and a hairline ring, so white on white is still an object."
              />
              <ColourField
                id={id('barsIconColor')}
                label="Icon"
                value={value.barsIconColor}
                onChange={(v) => set({ barsIconColor: v })}
                hint="The mic and stop glyph. Empty follows the accent, which is how the reference draws it."
              />
              <ColourField
                id={id('barsCaptionColor')}
                label="Caption"
                value={value.barsCaptionColor}
                onChange={(v) => set({ barsCaptionColor: v })}
                hint="The caption sits under the stage, on the host page's own background — so it is neutral by default rather than accent-coloured."
              />
            </div>
          </Section>
        </>
      )}

      {variant === 'orb' && (
        <Section title="Orb">
          <div className="field">
            <Label>Style</Label>
            <ToggleGroup
              type="single"
              variant="outline"
              size="sm"
              spacing={0}
              value={value.orbTheme}
              // Radix hands back '' when somebody re-presses the active item;
              // ignoring that keeps the orb in a style rather than none.
              onValueChange={(v) => {
                if (v === 'cloud' || v === 'circle' || v === 'radial') set({ orbTheme: v })
              }}
            >
              <ToggleGroupItem value="cloud">Cloud</ToggleGroupItem>
              <ToggleGroupItem value="circle">Circle</ToggleGroupItem>
              <ToggleGroupItem value="radial">Radial</ToggleGroupItem>
            </ToggleGroup>
            <p className="text-xs text-muted-foreground">
              Cloud and radial draw with WebGL and fall back to the circle on pages
              without it.
            </p>
          </div>
          <div className="field">
            <Label htmlFor={id('orbSize')}>
              Size
              <span className="ml-auto text-xs font-normal text-muted-foreground">
                {value.orbSize}px
              </span>
            </Label>
            {/* A native range, styled. There is no Slider in ui/, and one knob is
                not worth adding a shadcn primitive and its Radix dependency. */}
            <input
              id={id('orbSize')}
              type="range"
              min={48}
              max={240}
              step={4}
              value={value.orbSize}
              onChange={(e) => set({ orbSize: Number(e.target.value) })}
              className="h-8 w-full accent-primary"
            />
          </div>
          <div className="grid gap-x-4 gap-y-3 sm:grid-cols-2">
            <ColourField
              id={id('colorFrom')}
              label="Gradient from"
              value={value.colors.from}
              onChange={(v) => setColour('from', v)}
            />
            <ColourField
              id={id('colorTo')}
              label="Gradient to"
              value={value.colors.to}
              onChange={(v) => setColour('to', v)}
            />
          </div>
        </Section>
      )}

      {variant === 'pill' && (
        <Section title="Hover pill">
          <div className="grid gap-x-4 gap-y-3 sm:grid-cols-2">
            <div className="field sm:col-span-2">
              <Label
                htmlFor={id('pillHoverLabel')}
                title="The word the orange sweep reveals, with the microphone beside it. The Label field above is the resting word — both are painted, one under the other."
              >
                Hover label
              </Label>
              <Input
                id={id('pillHoverLabel')}
                value={value.pillHoverLabel}
                maxLength={LABEL_MAX}
                onChange={(e) => set({ pillHoverLabel: e.target.value })}
              />
              <p className="text-xs text-muted-foreground">
                It is also the button&rsquo;s screen-reader name before the call starts,
                because it is the one that says what a click will do.
              </p>
            </div>
            <NumberField
              id={id('pillWidth')}
              label="Width"
              value={value.pillWidth}
              min={120}
              max={360}
              hint="176 is the reference button's own width."
              onChange={(n) => set({ pillWidth: n })}
            />
            <NumberField
              id={id('pillHeight')}
              label="Height"
              value={value.pillHeight}
              min={36}
              max={72}
              hint="The corner radius is always half of this, so it stays a capsule."
              onChange={(n) => set({ pillHeight: n })}
            />
            <ColourField
              id={id('pillSurface')}
              label="Resting fill"
              value={value.pillSurface}
              onChange={(v) => set({ pillSurface: v })}
            />
            <ColourField
              id={id('pillOnSurface')}
              label="Resting ink"
              value={value.pillOnSurface}
              onChange={(v) => set({ pillOnSurface: v })}
            />
            <ColourField
              id={id('pillBorder')}
              label="Ring"
              value={value.pillBorder}
              onChange={(v) => set({ pillBorder: v })}
              hint="The 2px outline. A third colour on purpose — indigo on white with an orange dot is what makes this button look like itself, and it is not an alpha of either of the other two."
            />
            <CheckField
              id={id('pillSound')}
              label="Click on hover"
              checked={value.pillSound}
              onChange={(v) => set({ pillSound: v })}
              hint="The reference button's own 46ms click, played when the pointer arrives — not on focus, so a keyboard user tabbing past a page does not hear it. Browsers refuse to play any audio until the page has seen a real click or keypress, so the first hover on a fresh page is silent whatever this says."
            />
            <CheckField
              id={id('pillAllowMute')}
              label="Mute button"
              checked={value.allowMute}
              onChange={(on) => set({ allowMute: on })}
              hint="A small disc at the right of the live row, hollow while the microphone is open and filled while muted — the flag's control. The meters give up its width. Holds the session open and stops sending audio; the pill itself is still the hang-up."
            />
            <NumberField
              id={id('pillMeterBars')}
              label="Meter bars"
              value={value.pillMeterBars}
              min={3}
              max={12}
              onChange={(n) => set({ pillMeterBars: n })}
              hint="Bars in each of the two live meters. 5 is the reference button's; a wider pill has room for 7."
            />
          </div>
          <p className="text-xs text-muted-foreground">
            Five colours in all, which is more than any other variant: the two above in{' '}
            <span className="font-medium">Appearance</span> are the sweep and the ink on
            it, and these three are the button at rest. There is no light/dark switch
            because these five <em>are</em> the theme — the widget contains no stylesheet
            and no class name for a switch to reach.
          </p>
        </Section>
      )}

      {variant === 'button' && (
        <Section title="Button">
          <div className="grid gap-x-4 gap-y-3 sm:grid-cols-2">
            <div className="field">
              <Label>Size</Label>
              <ToggleGroup
                type="single"
                variant="outline"
                size="sm"
                spacing={0}
                value={value.buttonSize}
                onValueChange={(v) => {
                  if (v === 'sm' || v === 'default' || v === 'lg') set({ buttonSize: v })
                }}
              >
                <ToggleGroupItem value="sm">sm</ToggleGroupItem>
                <ToggleGroupItem value="default">default</ToggleGroupItem>
                <ToggleGroupItem value="lg">lg</ToggleGroupItem>
              </ToggleGroup>
            </div>
            <PlacementField
              id={id('placement')}
              value={value.placement}
              onChange={(p) => set({ placement: p })}
            />
          </div>
          <CheckField
            id={id('showTimer')}
            label="Show the timer"
            checked={value.showTimer}
            onChange={(on) => set({ showTimer: on })}
            hint="Counts up from the moment the call connected, not from the click."
          />
        </Section>
      )}

      {/* ---------------------------------------------------- allowed origins */}
      {/*
        NO SAVE BUTTON AND NO KEY ON DISPLAY (product owner, 2026-08-23). The
        snippet is a pure function of the settings above, its key is minted
        automatically per widget type, and edits here persist on their own —
        so the only thing this card shows is the one server-side fact a human
        actually manages: where the widget may run.
      */}
      {canWrite && (
        <Section title="Allowed origins">
          <p className="text-xs text-muted-foreground">
            The one server-side setting, per widget type: the pages allowed to run this
            variant&rsquo;s snippet. Everything else — appearance, the agent
            (<span className="mono">data-agent-id</span>), visitor facts
            (<span className="mono">data-client-payload</span>) — rides the tag itself.
            Changes here are saved automatically.
          </p>

          <OriginsEditor variant={variant} origins={origins} onChange={onOriginsChange} />

          {/* Fixed height so the card does not jump as saves come and go. */}
          <p className="h-4 text-xs" aria-live="polite">
            {originsSave?.kind === 'saving' && <span className="text-muted-foreground">Saving…</span>}
            {originsSave?.kind === 'saved' && <span className="text-muted-foreground">Saved.</span>}
            {originsSave?.kind === 'error' && (
              <span className="text-ev-bad">Could not save: {originsSave.message}</span>
            )}
          </p>
        </Section>
      )}
    </div>
  )
}

/**
 * The origin allow-list.
 *
 * THE EMPTY-LIST WARNING IS NOT DECORATION. An empty list means the widget runs
 * nowhere — that is the fail-closed default, it is the right default, and it is
 * also the single thing everybody hits on their first embed and reads as a broken
 * key. So it is stated as a consequence rather than as a hint.
 */
export function OriginsEditor({
  variant,
  origins,
  onChange,
}: {
  variant: EmbedVariant
  origins: string[]
  /**
   * An updater, and for a reason found by driving the page rather than by reading
   * it: `onChange([...origins, ''])` closes over the list from the render it was
   * built in, so several presses of "Add an origin" inside one React batch all
   * append to the same base list and add ONE row between them. Every write below
   * derives from `prev`.
   */
  onChange: (update: (prev: string[]) => string[]) => void
}) {
  const id = (n: number) => `${variant}-origin-${n}`
  return (
    <div className="field">
      <Label>Allowed origins</Label>

      {origins.length === 0 ? (
        <p className="rounded-md border border-ev-warn-border bg-ev-warn-bg px-3 py-2 text-xs text-ev-warn">
          <strong className="font-semibold">Empty means nowhere.</strong> With no origins
          listed the widget will refuse to start on every page, including the one you are
          testing from. That is deliberate — a key that works anywhere is a key anybody can
          lift out of your page source and run up a bill with — but it does mean a new embed
          does nothing until you add at least one.
        </p>
      ) : (
        <div className="flex flex-col gap-1.5">
          {origins.map((o, i) => {
            const problem = originProblem(o)
            return (
              <div key={id(i)} className="flex flex-col gap-1">
                <div className="flex items-center gap-1.5">
                  <Input
                    id={id(i)}
                    value={o}
                    aria-invalid={problem !== null}
                    aria-label={`Allowed origin ${i + 1}`}
                    placeholder="https://example.com"
                    className="mono"
                    onChange={(e) =>
                      onChange((prev) => prev.map((v, j) => (j === i ? e.target.value : v)))
                    }
                  />
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon-sm"
                    title="Remove this origin"
                    aria-label={`Remove origin ${i + 1}`}
                    onClick={() => onChange((prev) => prev.filter((_, j) => j !== i))}
                  >
                    <XIcon />
                  </Button>
                </div>
                {problem && <p className="text-xs text-ev-bad">{problem}</p>}
              </div>
            )
          })}
        </div>
      )}

      <div className="flex flex-wrap items-center gap-2">
        <Button
          type="button"
          variant="outline"
          size="xs"
          onClick={() => onChange((prev) => [...prev, ''])}
        >
          <PlusIcon /> Add an origin
        </Button>
      </div>
      <p className="text-xs text-muted-foreground">
        A bare origin each: scheme, host and port, no path and no trailing slash. Rejected
        rather than tidied up, so what is stored is what you typed.
      </p>
    </div>
  )
}

// ===========================================================================
// Small shared field shapes. Each has three or more callers above.
// ===========================================================================

/** A self-contained bordered card; the editor is a stack of these. */
function Section({ title, children }: { title: string; children: ReactNode }) {
  return (
    <div className="flex min-w-0 flex-col gap-3 rounded-md border p-4">
      <h3 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
        {title}
      </h3>
      {children}
    </div>
  )
}

/**
 * A CSS colour as text, with a swatch that follows it when it can.
 *
 * The swatch is `<input type="color">`, which only speaks `#rrggbb`. When the text
 * is anything else the swatch stops tracking and the form says so, rather than
 * rewriting the value. See the file header.
 */
function ColourField({
  id,
  label,
  value,
  onChange,
  hint,
}: {
  id: string
  label: string
  value: string
  onChange: (v: string) => void
  hint?: string
}) {
  const hex = /^#[0-9a-fA-F]{6}$/.test(value)
  return (
    <div className="field">
      <Label htmlFor={id}>{label}</Label>
      <div className="flex items-center gap-1.5">
        <input
          type="color"
          aria-label={`${label} swatch`}
          value={hex ? value.toLowerCase() : '#000000'}
          onChange={(e) => onChange(e.target.value)}
          className="size-8 shrink-0 cursor-pointer rounded-lg border border-input bg-transparent p-0.5"
        />
        <Input id={id} value={value} onChange={(e) => onChange(e.target.value)} className="mono" />
      </div>
      {!hex && value.trim() !== '' && (
        <p className="text-xs text-muted-foreground">
          Not 6-digit hex, so the swatch is not following it. The value is used as typed.
        </p>
      )}
      {hint && <p className="text-xs text-muted-foreground">{hint}</p>}
    </div>
  )
}

function NumberField({
  id,
  label,
  value,
  min,
  max,
  step,
  hint,
  onChange,
}: {
  id: string
  label: string
  value: number
  min: number
  max: number
  step?: number
  hint?: string
  onChange: (n: number) => void
}) {
  return (
    <div className="field">
      <Label htmlFor={id}>{label}</Label>
      <Input
        id={id}
        type="number"
        min={min}
        max={max}
        {...(step === undefined ? {} : { step })}
        value={value}
        // An empty box parses to NaN, which would reach the widget as a broken
        // CSS length. The floor is used instead until they type a digit.
        onChange={(e) => onChange(Number.isFinite(e.target.valueAsNumber) ? e.target.valueAsNumber : min)}
        className="mono"
      />
      {hint && <p className="text-xs text-muted-foreground">{hint}</p>}
    </div>
  )
}

function CheckField({
  id,
  label,
  checked,
  onChange,
  hint,
}: {
  id: string
  label: string
  checked: boolean
  onChange: (on: boolean) => void
  hint?: string
}) {
  return (
    <div className="field">
      <label htmlFor={id} className="flex cursor-pointer items-center gap-2 text-sm font-medium">
        <Checkbox id={id} checked={checked} onCheckedChange={(v) => onChange(v === true)} />
        {label}
      </label>
      {hint && <p className="text-xs text-muted-foreground">{hint}</p>}
    </div>
  )
}

function PlacementField({
  id,
  value,
  onChange,
  /** Drop `inline` from the list. For a launcher there is no such placement — "a
   *  corner-parked call button, in this div" is not a thing — and offering it would
   *  be offering a value the widget reads as `bottom-right`. */
  cornersOnly = false,
  hint,
}: {
  id: string
  value: VoicePlacement
  onChange: (p: VoicePlacement) => void
  cornersOnly?: boolean
  hint?: ReactNode
}) {
  const options = cornersOnly ? CORNERS.filter((p) => p !== 'inline') : CORNERS
  return (
    <div className="field">
      <Label htmlFor={id}>Placement</Label>
      <Select value={value} onValueChange={(v) => onChange(v as VoicePlacement)}>
        <SelectTrigger id={id} size="sm" className="w-full">
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          {options.map((p) => (
            <SelectItem key={p} value={p}>
              <span className="mono">{p}</span>
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
      <p className="text-xs text-muted-foreground">
        {hint ?? (
          <>
            The four corners float over the page; <span className="mono">inline</span> puts the
            widget where the customer&rsquo;s own markup puts it.
          </>
        )}
      </p>
    </div>
  )
}
